import React, { FunctionComponent, KeyboardEvent, KeyboardEventHandler, ReactNode, useCallback, useRef, useState } from "react";
import classNames from "classnames";
import ButtonBase from "@mui/material/ButtonBase";
import Popper from "@mui/material/Popper";
import ExpandMenuIcon from "mdi-material-ui/MenuDown";

import "../../../css/filter_bar/dropdown_below.css";
import FilterBarFlyout from "./filter_bar_flyout";
import TooltippedOnOverflow from "../shared/tooltipped_on_overflow";

export type Props = {
  className?: string
  disabled?: boolean
  open: boolean
  // A plain Popper, not a Popover: no modal backdrop to eat the click meant for the viewer.
  persistent?: boolean
  closeFlyout: () => void
  openFlyout: (event: React.MouseEvent) => void
  buttonContent: ReactNode
  children: ReactNode
  // Overridable so the second-level nav can use the chevron its design calls for; the default
  // keeps the filter bar unchanged.
  icon?: ReactNode
  label?: string
  onFlyoutEntering?: () => void
  onFlyoutEntered?: () => void
  handleOpenPreviousMenu?: KeyboardEventHandler
  handleOpenNextMenu?: KeyboardEventHandler
};

const DropdownBelow: FunctionComponent<Props> = (props) => {
  const { children, open, persistent, handleOpenNextMenu, handleOpenPreviousMenu, openFlyout, closeFlyout } = props;
  // State, not a ref: a ref set during render never re-renders the Popper onto its anchor.
  const [anchor, setAnchor] = useState<HTMLElement>(null);
  const anchorElement = useRef();
  const handleSelectMenuButton = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (open) {
      closeFlyout();
    } else {
      openFlyout(event);
    }
  }, [open, openFlyout, closeFlyout]);
  const handleMenuButtonKeyDown = useCallback((event: KeyboardEvent) => {
    switch (event.key) {
      case "Enter":
      case " ":
      case "ArrowUp":
      case "ArrowDown": {
        handleSelectMenuButton(event);
        break;
      }
      case "ArrowLeft": {
        handleOpenPreviousMenu && handleOpenPreviousMenu(event);
        break;
      }
      case "ArrowRight": {
        handleOpenNextMenu && handleOpenNextMenu(event);
        break;
      }
      default: {}
    }
  }, [handleSelectMenuButton, handleOpenNextMenu, handleOpenPreviousMenu]);
  return <>
    <ButtonBase
      className={classNames("DropdownBelow", props.className)}
      ref={persistent ? setAnchor : anchorElement}
      component="div"
      aria-expanded={open}
      disabled={props.disabled}
      onClick={handleSelectMenuButton}
      onKeyDown={handleMenuButtonKeyDown}
      focusRipple
    >
      <span className="DropdownBelow-label">{props.label}</span>
      <div className="DropdownBelow-buttonContent">
        <TooltippedOnOverflow>
          {props.buttonContent}
        </TooltippedOnOverflow>
      </div>
      {props.disabled ? null : props.icon ?? <ExpandMenuIcon
        className="DropdownBelow-icon"
        aria-hidden="true"
      />}
    </ButtonBase>
    {persistent ? <Popper
      className="DropdownBelow-persistentFlyout"
      open={open}
      anchorEl={anchor}
      placement="bottom-end"
    >
      {children}
    </Popper> : <FilterBarFlyout
      open={open}
      onClose={closeFlyout}
      TransitionProps={{
        onEntering: props.onFlyoutEntering,
        onEntered: props.onFlyoutEntered
      }}
      anchorEl={anchorElement.current}
    >
      {children}
    </FilterBarFlyout>}
  </>;
};

export default DropdownBelow;
